var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Sutl } from '../../../sutl';
import { Timeouts } from './framework/Timeouts';
const uSwitchChannelKeys = [
    // -e means on a linked device
    'ADCin1-i',
    'GPIO1-i',
    'GPIO2-i',
    'GPIO3-i',
    'GPIO3-e',
    'GPIO4-e',
    'GPIO5-e',
    'GPIO6-e',
    'GPIO7-e',
    'GPIO8-e',
    'GPIO9-e',
    'GPIO10-e',
    'Relay1-i',
    'Relay2-i',
    'Relay3-e',
    'Relay4-e',
    'Relay5-e',
    'Relay6-e',
    'Relay7-e',
    'Relay8-e',
    'Relay9-e',
    'Relay10-e',
];
const uSwitchChannelTypes = [
    { type: 'relay', field: 'Relays' },
    { type: 'digitalIn', field: 'GPIOs' },
    { type: 'analogIn', field: 'ADCs' },
];
function isUSwitchClosureConfig(config) {
    return 'name' in config;
}
export class USwitch {
    constructor(http, state, log) {
        this.http = http;
        this.state = state;
        this.log = log;
        // other class properties and constructor
        this.channels = [];
        this.timeouts = new Timeouts();
        // controls
        this.busy = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.busy.value;
            }),
        };
        this.contactClosure = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                var _a;
                return (_a = this.state.contactClosures.values[key]) !== null && _a !== void 0 ? _a : false;
            }),
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                var _b;
                const resp = yield this.httpGet(`/UHCapi.xml?cmd=relay&${key}=${value ? '1' : '0'}`, tags);
                const stateRegExp = new RegExp(`<${key}>(.+)</${key}>`);
                const stateMatch = stateRegExp.exec(resp);
                if (stateMatch)
                    return stateMatch[1].toLowerCase() === 'on';
                return (_b = this.state.contactClosures.values[key]) !== null && _b !== void 0 ? _b : false;
            }),
            getKey: (config) => {
                if (!isUSwitchClosureConfig(config)) {
                    throw new Error(`invalid contact closure config: ${JSON.stringify(config)}`);
                }
                return `${config.name}`;
            },
            validate: (config) => __awaiter(this, void 0, void 0, function* () {
                if (!isUSwitchClosureConfig(config)) {
                    this.log.error(`invalid contact closure config: ${JSON.stringify(config)}`);
                    return { name: 'missing' };
                }
                for (const chan of this.channels) {
                    if (chan.name === config.name) {
                        return { name: 'ok' };
                    }
                }
                return { name: 'missing' };
            }),
        };
        this.detected = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return { channels: [...this.channels] };
            }),
        };
        // polling
        this.polls = [
            {
                intervalMs: 4000,
                function: () => __awaiter(this, void 0, void 0, function* () {
                    yield this.updateStates(['poll', 'contactClosure']);
                }),
            },
            {
                intervalMs: 30000,
                function: () => __awaiter(this, void 0, void 0, function* () {
                    const channels = yield this.getChannels(['poll', 'detected']);
                    // Assume channels are sorted so we can compare with objEquals.
                    if (!Sutl.objEquals(channels, this.channels)) {
                        this.channels.length = 0;
                        this.channels.push(...channels);
                        this.state.detected.update({ channels: this.channels });
                    }
                }),
            },
        ];
    }
    // phase callbacks
    onConfigure(config) {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
            this.channels.length = 0;
            const channels = yield this.getChannels(['configure', 'detected']);
            this.channels.push(...channels);
            yield this.updateStates(['configure', 'contactClosure']);
        });
    }
    onDisconnect() {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
        });
    }
    onInitialize(credentials) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const resp = yield this.httpGet(`/UHCapi.xml?GetAllNames`, ['initialize']);
                if (resp.includes('Relays')) {
                    return 'connected';
                }
                else {
                    return 'verifyFailed';
                }
            }
            catch (error) {
                return 'connectFailed';
            }
            return 'connectFailed';
        });
    }
    // other class methods
    getChannels(tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.httpGet(`/UHCapi.xml?GetAllNames`, tags);
            return this.parseChannels(resp, 'relay').sort();
        });
    }
    httpGet(path, tags) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield this.http.request('get', `${path}`, Object.assign({}, (tags ? { tags } : undefined)));
            return (_a = resp.data) !== null && _a !== void 0 ? _a : '';
        });
    }
    parseChannels(data, ioType) {
        const channels = [];
        for (const chanType of uSwitchChannelTypes) {
            if (chanType.type === ioType) {
                const chanTypeRegExp = new RegExp(`<${chanType.field}>(.+)</${chanType.field}>`);
                const filters = {};
                const chanTypeMatch = chanTypeRegExp.exec(data);
                if (chanTypeMatch && chanTypeMatch[1]) {
                    for (const key of uSwitchChannelKeys) {
                        const channelRegExp = new RegExp(`<${key}>(.+)</${key}>`);
                        const match = channelRegExp.exec(chanTypeMatch[1]);
                        if (match && match[1]) {
                            filters.contactClosures = true;
                            channels.push({ filters, name: match[1], type: 'relay' });
                        }
                    }
                }
                break;
            }
        }
        return channels;
    }
    updateStates(tags) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const data = yield this.httpGet('/UHCapi.xml?cmd=relay&GetAll', tags);
            for (const chan of this.channels) {
                const name = (_a = chan.name) !== null && _a !== void 0 ? _a : '';
                const stateRegExp = new RegExp(`<${name}>(.+)</${name}>`);
                const stateMatch = stateRegExp.exec(data);
                if (stateMatch && stateMatch[1] && name in this.state.contactClosures.values) {
                    this.state.contactClosures.update(name, stateMatch[1].toLowerCase() === 'on');
                }
            }
        });
    }
}
// static properties
USwitch.driverType = 'httpDriver';
USwitch.key = '-NZ7IIdHzzRXWg-sMQ6K';
USwitch.httpRequestOptions = {
    prettyCommands: false,
    scheme: 'http',
};
